import { measureLatency } from '../../gctl-measure';
import {
  VideoTranslationFields,
  VideoTranslation,
} from '../../models/components/schemas/video-translation/VideoTranslation';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoTranslationsFields = readonly VideoTranslationFields[];

/** Options for GET /videos/{video_id}/translations */
export type GetVideoTranslationsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.translations+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/translations */
  query?: {
    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;
  };
};

/** Result type for GET /videos/{video_id}/translations */
export type GetVideoTranslationsResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /videos/{video_id}/translations */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<VideoTranslation, Paths>[];
};

/**
 * Get a list of video translation jobs for the user
 * This method returns a list of the specified video's translation jobs for the authenticated user.
 */
export async function getVideoTranslations<Paths extends GetVideoTranslationsFields>({
  baseUrl,
  select,
  where: { videoId },
  query,
  ...options
}: GetVideoTranslationsOptions<Paths>): Promise<GetVideoTranslationsResult<Paths>> {
  return measureLatency('getVideoTranslations', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/translations?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetVideoTranslationsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostVideoTranslationsFields = readonly string[];

/** Options for POST /videos/{video_id}/translations */
export type PostVideoTranslationsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Data serialized to body for POST /videos/{video_id}/translations */
  variables?: {
    /**
     * Whether to disable the feature for cloning the speaker's voice.
     */

    disableVoiceCloning?: boolean;

    /**
     * The ID of the translation job group.
     * @example 1234
     */

    groupId?: string;

    /**
     * The UUID of the translation job.
     * @example bc31e0ff-4351-46a7-854b-668b78a6bae8
     */

    jobId?: string;

    /**
     * The number of speakers in the video for the translation.
     */

    numSpeakers?: number;

    /**
     * Whether to override the video summary.
     * @example true
     */

    overrideCache?: boolean;

    /**
     * An array of IDs of the target text track's segments to synchronize with the source.
     */

    segmentsIds?: string[];

    /**
     * The operational context of the translation service.
     *
     * Option descriptions:
     *  * `full_apply` - Translate and save for the target language and video.
     *  * `preview` - Generate a preview for the target language and video.
     *  * `regenerate` - Sync specified segments with the parent text track.
     *
     */

    serviceMode?: `full_apply` | `preview` | `regenerate`;

    /**
     * The target accent of the translation job.
     * @example cockney
     */

    targetAccent?: string;

    /**
     * The target language of the translation job. For a full list of supported languages, use the [`/languages?filter=audiotracks`](https://developer.vimeo.com/api/reference/videos#get_languages) method.
     * @example en-US
     */

    targetLang?: string;

    /**
     * The ID of text track to synchronize with the source.
     * @example 161406796
     */

    targetTexttrackId?: number;

    /**
     * The ID of the text-to-text translation source track.
     * @example 161406796
     */

    texttrackId?: number;

    /**
     * The type of translation output to be generated.
     *
     * Option descriptions:
     *  * `dubbing` - Generate both subtitles and audio.
     *  * `subtitling` - Generate subtitles only.
     *
     */

    translationMode?: `dubbing` | `subtitling`;
  };
};

/** Result type for POST /videos/{video_id}/translations */
export type PostVideoTranslationsResult<Paths extends readonly string[]> = unknown;

/**
 * Start a video translation job for the user
 * This method starts a translation job of the specified video for the authenticated user.
 */
export async function postVideoTranslations<Paths extends PostVideoTranslationsFields>({
  baseUrl,
  variables,
  where: { videoId },
  ...options
}: PostVideoTranslationsOptions<Paths>): Promise<PostVideoTranslationsResult<Paths>> {
  return measureLatency('postVideoTranslations', 'POST', async () => {
    let res = await fetch(`${baseUrl}/videos/${videoId}/translations`, {
      ...options,
      method: 'POST',
      body: JSON.stringify(deepSnakeCase(variables)),
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostVideoTranslationsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type DeleteVideoTranslationsFields = readonly string[];

/** Options for DELETE /videos/{video_id}/translations */
export type DeleteVideoTranslationsOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Data serialized to body for DELETE /videos/{video_id}/translations */
  variables?: {
    /**
     * The generation mode of the existing translation track. + Members
     * *
     * *
     *
     * Option descriptions:
     *  * `full_apply` - The translation track was generated as a full-length saved version.
     *  * `preview` - The translation track was generated in preview mode.
     *
     */

    serviceMode?: `full_apply` | `preview`;

    /**
     * The target language of the translation.
     * @example en-US
     */

    targetLang?: string;
  };
};

/** Result type for DELETE /videos/{video_id}/translations */
export type DeleteVideoTranslationsResult<Paths extends readonly string[]> = unknown;

/**
 * Delete an existing translation track for a video
 * This method deletes an existing translation track for the specified video.
 */
export async function deleteVideoTranslations<Paths extends DeleteVideoTranslationsFields>({
  baseUrl,
  variables,
  where: { videoId },
  ...options
}: DeleteVideoTranslationsOptions<Paths>): Promise<DeleteVideoTranslationsResult<Paths>> {
  return measureLatency('deleteVideoTranslations', 'DELETE', async () => {
    let res = await fetch(`${baseUrl}/videos/${videoId}/translations`, {
      ...options,
      method: 'DELETE',
      body: JSON.stringify(deepSnakeCase(variables)),
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as DeleteVideoTranslationsResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
